import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import * as Hooks from '../hooks';
import { useAuthContext } from 'modules/auth/context';

const createSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().min(1, 'Description is required'),
  duration: z.number().min(1, 'Duration must be at least 1 minute'),
  passingScore: z.number().min(0).max(100, 'Passing score must be between 0 and 100'),
  settings: z.object({
    shuffleQuestions: z.boolean().default(false),
    shuffleAnswers: z.boolean().default(false),
    showResultsImmediately: z.boolean().default(true),
    allowReview: z.boolean().default(true),
    proctoringEnabled: z.boolean().default(false),
    requireCamera: z.boolean().default(false),
    requireMicrophone: z.boolean().default(false),
    fullScreenMode: z.boolean().default(false),
    preventCopyPaste: z.boolean().default(false),
  }),
});

type FormValues = z.infer<typeof createSchema>;

interface IProps {
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
  children: (props: {
    form: ReturnType<typeof useForm<FormValues>>;
    isSubmitting: boolean;
    handleSubmit: (e?: React.BaseSyntheticEvent) => void;
  }) => JSX.Element;
}

const Create: React.FC<IProps> = ({ onSuccess, onError, children }) => {
  const mutation = Hooks.useCreate();
  const { state } = useAuthContext();
  const organizationId = state.organization?.id;

  const form = useForm<FormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      title: '',
      description: '',
      duration: 60,
      passingScore: 70,
      settings: {
        shuffleQuestions: false,
        shuffleAnswers: false,
        showResultsImmediately: true,
        allowReview: true,
        proctoringEnabled: false,
        requireCamera: false,
        requireMicrophone: false,
        fullScreenMode: false,
        preventCopyPaste: false,
      },
    },
  });

  const onSubmit = form.handleSubmit((values: FormValues) => {
    if (!organizationId) {
      onError?.('Your account is not linked to an organization. Please contact support.');
      return;
    }
    mutation.mutate(
      {
        title: values.title,
        description: values.description,
        organizationId,
        language: 'uz',
        settings: {
          timeLimit: values.duration,
          passingScore: values.passingScore,
          shuffleQuestions: values.settings.shuffleQuestions,
          shuffleOptions: values.settings.shuffleAnswers,
          showResultsImmediately: values.settings.showResultsImmediately,
        },
      },
      {
        onSuccess: (data) => {
          form.reset();
          onSuccess?.(data);
        },
        onError: (error: any) => {
          onError?.(error?.response?.data?.message || 'Failed to create test');
        },
      }
    );
  });

  return children({
    form,
    isSubmitting: mutation.isPending,
    handleSubmit: onSubmit,
  });
};

export default Create;
